import { Navigate, Route, Routes, useLocation } from "react-router";
import Login from "./login/Login";
import SimpleSundayServiceForm from "./sundayservice/SimpleSundayServiceForm";
import SimpleSundayServiceList from "./sundayservice/SimpleSundayServiceList";
import SermonInfoForm from "./sundayservice/SermonInfoForm";
import RecordingNamesPage, { loadRecordingServiceData } from "./sundayservice/RecordingNamesPage";
import AppLayout from "./outlets/AppLayout";
import Container from "react-bootstrap/esm/Container";
import InfoMessage from "./infos/InfoMessage";
import { useState } from "react";
import { Variant } from "react-bootstrap/esm/types";
import { Globals } from "./globals";
import AdminPanel from "./admin/AdminPanel";
import SuperadminPanel from "./superadmin/SuperadminPanel";
import GemeindeSettings from "./gemeinde/GemeindeSettings";
import NoGemeinde from "./auth/NoGemeinde";
import Datenschutz from "./legal/Datenschutz";
import Nutzungsbedingungen from "./legal/Nutzungsbedingungen";
import Impressum from "./legal/Impressum";
import DataDeletionRequest from "./legal/DataDeletionRequest";
import MyPlanRequest from "./myplan/MyPlanRequest";
import MyPlanPage from "./myplan/MyPlanPage";
import ChoirPlanPage from "./choirplan/ChoirPlanPage";
import SwapPage from "./myplan/SwapPage";
import CookieConsentBanner from "./consent/CookieConsentBanner";
import BiblePassageRobotDemo from "./robot/BiblePassageRobotDemo";
import NotFound from "./errors/NotFound";
import RecordingNamesRobotDemo from "./robot/RecordingNamesRobotDemo";

/** Redirects a pre-rename path to its new one, keeping query string, hash and state. */
function LegacyRedirect({ to }: { to: string }) {
    const location = useLocation();
    return <Navigate replace to={{ pathname: to, search: location.search, hash: location.hash }} state={location.state} />;
}

export default function App() {
    const [variant, setVariant] = useState<Variant>("danger");
    const [message, setMessage] = useState<string>("");
    const [showMessage, setShowMessage] = useState(false);

    // The demo pages for the Robot Framework tests: in development, and in the
    // production build the test workflow makes (REACT_APP_ROBOT_ROUTES=true).
    const showRobotRoutes = process.env.NODE_ENV !== "production" || process.env.REACT_APP_ROBOT_ROUTES === "true";

    // The authenticated app (login, service list/form, admin, legal pages) is
    // rendered inside the AppLayout layout, which provides the shared sidebar.
    // The public/bare routes below sit outside it and have no chrome.
    return (
        <>
            <Container fluid className="px-0">
                <Routes>
                    <Route path={Globals.PATH_DEFAULT} element={<AppLayout />}>
                        <Route
                            index
                            element={<Login setVariant={setVariant} setMessage={setMessage} setShowMessage={setShowMessage} />}
                        />
                        <Route
                            path={Globals.PATH_SIMPLE_SUNDAY_SERVICE}
                            element={
                                <SimpleSundayServiceForm
                                    setVariant={setVariant}
                                    setMessage={setMessage}
                                    setShowMessage={setShowMessage}
                                />
                            }
                        />
                        <Route
                            path={Globals.PATH_SIMPLE_SUNDAY_SERVICE_LIST}
                            element={
                                <SimpleSundayServiceList
                                    setVariant={setVariant}
                                    setMessage={setMessage}
                                    setShowMessage={setShowMessage}
                                />
                            }
                        />
                        <Route
                            path={Globals.PATH_ADMIN}
                            element={
                                <AdminPanel setVariant={setVariant} setMessage={setMessage} setShowMessage={setShowMessage} />
                            }
                        />
                        <Route
                            path={Globals.PATH_SUPERADMIN}
                            element={
                                <SuperadminPanel setVariant={setVariant} setMessage={setMessage} setShowMessage={setShowMessage} />
                            }
                        />
                        <Route
                            path={Globals.PATH_GEMEINDE_SETTINGS}
                            element={
                                <GemeindeSettings setVariant={setVariant} setMessage={setMessage} setShowMessage={setShowMessage} />
                            }
                        />
                        <Route path={Globals.PATH_NO_GEMEINDE} element={<NoGemeinde />} />
                        <Route path={Globals.PATH_DATENSCHUTZ} element={<Datenschutz />} />
                        <Route path={Globals.PATH_NUTZUNGSBEDINGUNGEN} element={<Nutzungsbedingungen />} />
                        <Route path={Globals.PATH_IMPRESSUM} element={<Impressum />} />
                    </Route>
                    {/* Old paths from before the Serva rename (bookmarks, old links) */}
                    <Route
                        path="/simpleSundayService"
                        element={<LegacyRedirect to={Globals.PATH_SIMPLE_SUNDAY_SERVICE} />}
                    />
                    <Route
                        path="/simpleSundayServiceList"
                        element={<LegacyRedirect to={Globals.PATH_SIMPLE_SUNDAY_SERVICE_LIST} />}
                    />
                    {/* Links registered on the Google OAuth consent screen (Cloud Console → Branding) */}
                    <Route path="/gdpr" element={<LegacyRedirect to={Globals.PATH_DATENSCHUTZ} />} />
                    <Route
                        path="/terms-and-conditions"
                        element={<LegacyRedirect to={Globals.PATH_NUTZUNGSBEDINGUNGEN} />}
                    />
                    {/* Public route for sermon info form (no navbar, no auth required) */}
                    <Route
                        path={`${Globals.PATH_SERMON_INFO}/:token`}
                        element={<SermonInfoForm />}
                    />
                    {/* Public: a preacher's personal plan (token in the path) and the page to request the link */}
                    <Route path={Globals.PATH_MY_PLAN} element={<MyPlanRequest />} />
                    <Route path={`${Globals.PATH_MY_PLAN}/:token`} element={<MyPlanPage />} />
                    {/* The conductor asks for the link through the same form as the preachers. */}
                    <Route path={Globals.PATH_CHOIR_PLAN} element={<MyPlanRequest />} />
                    <Route path={`${Globals.PATH_CHOIR_PLAN}/:token`} element={<ChoirPlanPage />} />
                    {/* Public: a colleague answers a swap request (token in the path) */}
                    <Route path={`${Globals.PATH_SWAP}/:token`} element={<SwapPage />} />
                    {/* Public route for data deletion requests (no navbar, no auth required) */}
                    <Route
                        path={Globals.PATH_DATA_DELETION_REQUEST}
                        element={<DataDeletionRequest />}
                    />
                    <Route
                        path={`${Globals.PATH_RECORDING_NAMES}/:gemeindeSlug`}
                        element={
                            <RecordingNamesPage
                                serviceLoader={loadRecordingServiceData}
                                setVariant={setVariant}
                                setMessage={setMessage}
                                setShowMessage={setShowMessage}
                            />
                        }
                    />
                    {showRobotRoutes && (
                        <>
                            <Route path="/__robot__/bible" element={<BiblePassageRobotDemo />} />
                            <Route path="/__robot__/gottesdienst-namen" element={<RecordingNamesRobotDemo />} />
                        </>
                    )}
                    {/* Anything else: "page not found" instead of an empty page (GOD-59) */}
                    <Route path="*" element={<NotFound />} />
                </Routes>
                <InfoMessage variant={variant} text={message} show={showMessage} setShow={setShowMessage} />
            </Container>
            <CookieConsentBanner />
        </>
    );
}
